import { useMemo } from 'react'
import { cn } from '../shared/utils/cn'

// Sub-types for sensors with multiple metrics (rainfall, flow meter)
export const SENSOR_SUB_TYPES = {
  rainfall: [
    { key: 'rainfallVolume', label: 'Amount', chartType: 'line' },
    { key: 'rainfallRate', label: 'Rate', chartType: 'line' },
    { key: 'rainfallTotal', label: 'Total', chartType: 'line' },
  ],
  flowMeter: [
    { key: 'flowVolume', label: 'Volume', chartType: 'line' },
    { key: 'flowRate', label: 'Flow Rate', chartType: 'line' },
    { key: 'flowTotal', label: 'Total Volume', chartType: 'line' },
  ],
} as const

export type SensorSubType =
  | typeof SENSOR_SUB_TYPES.rainfall[number]['key']
  | typeof SENSOR_SUB_TYPES.flowMeter[number]['key']

// Sensor types that can be compared across devices
export const COMPARABLE_SENSOR_TYPES = [
  { key: 'temperature', label: 'Temperature', productNames: ['Temperature Sensor'] },
  { key: 'humidity', label: 'Humidity', productNames: ['Humidity'] },
  { key: 'windSpeed', label: 'Wind Speed', productNames: ['Wind Speed'] },
  { key: 'solarRadiation', label: 'Solar Radiation', productNames: ['Solar Radiation'] },
  { key: 'soilMoistureVH400', label: 'Soil Moisture (VH400)', productNames: ['Vegetronix VH400'] },
  { key: 'soilMoistureIrrometer', label: 'Soil Moisture (Irrometer)', productNames: ['Irrometer WM200SS'] },
  { key: 'soilTemperature', label: 'Soil Temp', productNames: ['Vegetronix THERM200'] },
  { key: 'battery', label: 'Battery', productNames: ['Battery'] },
  // Port C power rail — device-level, no physical port config (see below).
  { key: 'portCPower', label: 'Port C Power', productNames: [] },
  { key: 'pressure', label: 'Pressure', productNames: ['Pressure'] },
  {
    key: 'rainfall',
    label: 'Rainfall',
    productNames: ['Rainfall', 'Optical Rain Sensor SAB', 'Optical Rain Sensor Power'],
    hasSubTypes: true,
    subTypeKey: 'rainfall' as const,
  },
  {
    key: 'flowMeter',
    label: 'Flow Meter',
    productNames: ['Badgermeter 220BR', 'DEM PaddleWheel', 'Badgermeter SDI'],
    hasSubTypes: true,
    subTypeKey: 'flowMeter' as const,
  },
] as const

export type ComparableSensorType = typeof COMPARABLE_SENSOR_TYPES[number]['key']

// Device-level series that aren't wired to a physical A1..C3 port. Temperature
// (board temp) lives at the well-known "T0" port in /readings-fast; battery and
// the Port C power rail both come from the separate /battery-readings endpoint
// ({ battery, power }). All are universal (every device reports them) and are
// offered as extra picks in compare mode.
export const TEMPERATURE_PORT = 'T0'
export const BATTERY_PORT = 'BATT'
export const POWER_PORT = 'PWR'
export const DEVICE_LEVEL_SENSORS = [
  { key: 'temperature', label: 'Temperature', port: TEMPERATURE_PORT },
  { key: 'battery', label: 'Battery', port: BATTERY_PORT },
  { key: 'portCPower', label: 'Port C Power', port: POWER_PORT },
] as const

// Resolve a device's sensor product name to its comparable-type config entry
// (or null if it isn't a comparable type). Shared by the compare grid and the
// multi-device readings hook so both agree on type detection.
export function getComparableTypeByProduct(productName: string) {
  return (
    COMPARABLE_SENSOR_TYPES.find(t =>
      (t.productNames as readonly string[]).includes(productName)
    ) ?? null
  )
}

// True for sensor types that expose sub-metrics (rainfall/flow). In mixed mode
// these are selected per-metric via "port~metric" tokens (see utils/portTokens).
export function typeHasSubTypes(productName: string): boolean {
  const t = getComparableTypeByProduct(productName)
  return !!t && 'hasSubTypes' in t && t.hasSubTypes === true
}

// Display label for a sub-metric, e.g. 'rainfallRate' -> 'Rate'.
export function getSubTypeLabel(subType: SensorSubType): string {
  const all = [...SENSOR_SUB_TYPES.rainfall, ...SENSOR_SUB_TYPES.flowMeter]
  return all.find(s => s.key === subType)?.label ?? subType
}

export interface DeviceSensorInfo {
  deviceId: string
  deviceName: string
  siteName?: string
  sensorConfigs: Record<string, {
    sensorProductName: string
    offset?: number // depth in inches
  }>
}

interface SensorTypeSelectorProps {
  deviceSensorInfos: DeviceSensorInfo[]
  selectedSensorType: string | null
  onSelectSensorType: (sensorType: string | null) => void
  className?: string
}

export function SensorTypeSelector({
  deviceSensorInfos,
  selectedSensorType,
  onSelectSensorType,
  className = '',
}: SensorTypeSelectorProps) {
  // Calculate which sensor types are available for comparison
  // A sensor type is available if ALL selected devices have at least one sensor of that type
  const sensorTypeAvailability = useMemo(() => {
    if (deviceSensorInfos.length === 0) {
      return COMPARABLE_SENSOR_TYPES.map(type => ({
        ...type,
        available: false,
        deviceCount: 0,
      }))
    }

    return COMPARABLE_SENSOR_TYPES.map(sensorType => {
      let deviceCount = 0

      for (const deviceInfo of deviceSensorInfos) {
        const hasSensor = Object.values(deviceInfo.sensorConfigs).some(config =>
          (sensorType.productNames as readonly string[]).includes(config.sensorProductName)
        )
        if (hasSensor) {
          deviceCount++
        }
      }

      return {
        ...sensorType,
        available: deviceCount === deviceSensorInfos.length, // All devices must have it
        deviceCount,
      }
    })
  }, [deviceSensorInfos])

  // Get available types (all devices have this sensor)
  const availableTypes = sensorTypeAvailability.filter(t => t.available)
  // Get partial types (some devices have this sensor)
  const partialTypes = sensorTypeAvailability.filter(t => t.deviceCount > 0 && !t.available)

  if (deviceSensorInfos.length < 2) {
    return (
      <div className={cn('px-4 py-3 bg-purple-50/50 border-b border-purple-100', className)}>
        <p className="text-sm text-purple-600">
          Select at least 2 devices to compare their sensors
        </p>
      </div>
    )
  }

  if (availableTypes.length === 0) {
    return (
      <div className={cn('px-4 py-3 bg-amber-50 border-b border-amber-100', className)}>
        <p className="text-sm text-amber-700">
          Selected devices have no sensors in common.
          {partialTypes.length > 0 && (
            <span className="text-amber-600 ml-1">
              Try selecting devices of the same type.
            </span>
          )}
        </p>
      </div>
    )
  }

  return (
    <div className={cn('px-4 py-3 bg-purple-50/50 border-b border-purple-100', className)}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-medium text-purple-700 uppercase tracking-wide">
          Select Sensor Type
        </span>
        {selectedSensorType && (
          <button
            onClick={() => onSelectSensorType(null)}
            className="text-xs text-purple-500 hover:text-purple-700 underline"
          >
            Clear
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {/* Available sensor types (all devices have them) */}
        {availableTypes.map(sensorType => (
          <button
            key={sensorType.key}
            onClick={() => onSelectSensorType(sensorType.key === selectedSensorType ? null : sensorType.key)}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-full border transition-colors',
              selectedSensorType === sensorType.key
                ? 'bg-purple-600 text-white border-purple-600'
                : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50'
            )}
          >
            {sensorType.label}
          </button>
        ))}

        {/* Partial sensor types (grayed out) */}
        {partialTypes.map(sensorType => (
          <button
            key={sensorType.key}
            disabled
            className="px-3 py-1.5 text-sm font-medium rounded-full border bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
            title={`Only ${sensorType.deviceCount} of ${deviceSensorInfos.length} devices have this sensor`}
          >
            {sensorType.label}
            <span className="ml-1 text-xs text-gray-300">
              ({sensorType.deviceCount}/{deviceSensorInfos.length})
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

// Helper function to get sensor port info for a specific sensor type from device configs
export function getSensorPortsForType(
  sensorType: ComparableSensorType,
  sensorConfigs: Record<string, { sensorProductName: string; offset?: number }>
): { port: string; depth: number | null }[] {
  const sensorTypeConfig = COMPARABLE_SENSOR_TYPES.find(t => t.key === sensorType)
  if (!sensorTypeConfig) return []

  const matchingPorts: { port: string; depth: number | null }[] = []

  for (const [port, config] of Object.entries(sensorConfigs)) {
    if ((sensorTypeConfig.productNames as readonly string[]).includes(config.sensorProductName)) {
      matchingPorts.push({
        port,
        depth: config.offset ?? null,
      })
    }
  }

  return matchingPorts
}
